/* eslint-disable no-mixed-spaces-and-tabs */
import { useCallback, useEffect, useRef, useState } from "react";
import Proximiio from "proximiio-js-library";
import { State } from "proximiio-js-library/lib/components/map/main";
import maplibregl, { LngLatLike } from "maplibre-gl";
import { useShallow } from "zustand/react/shallow";
import {
  osName,
  browserName,
  mobileModel,
  mobileVendor,
  deviceType,
} from "react-device-detect";
import useRouting from "@/hooks/useRouting";
import { FilterItemModel } from "@/models/filterItem.model";
import i18n from "@/i18n";

import useMapStore from "@/store/mapStore";
import {
  buildAdditionalFilters,
  getHiddenItems,
  kiosks as predefinedKiosks,
} from "@/store/data";
import Feature from "proximiio-js-library/lib/models/feature";
import {
  cn,
  compareValues,
  compassHeading,
  getPolygonCentroid,
  getWayfindingConfig,
} from "@/lib/utils";
import useFeaturesStore from "@/store/featuresStore";
import { FeatureCollection } from "geojson";
import { useIdle } from "react-use";
import { t } from "i18next";
import { KioskModel } from "@/models/kiosk.model";
import MapFailedAlert from "./MapFailedAlert";
import { useFetchActiveAds } from "@/hooks/useFetchActiveAds";
import useDebugger from "@/hooks/useDebugger";

function MapView() {
  const mapInitiated = useRef(false);
  const [findRoute] = useRouting();
  const defaultPlaceId = import.meta.env.VITE_WAYFINDING_DEFAULT_PLACE_ID;
  const refetchData =
    import.meta.env.VITE_WAYFINDING_AUTO_DATA_REFETCH === "true";
  const autoRouting =
    import.meta.env.VITE_WAYFINDING_KIOSK_AUTO_ROUTING === "true";
  const landmarkTBT =
    import.meta.env.VITE_WAYFINDING_ENABLE_LANDMARK_TBT === "true";

  // store state
  const kioskMode = useMapStore((state) => state.kioskMode);
  const gpsMode = useMapStore((state) => state.gpsMode);
  const currentLang = useMapStore((state) => state.currentLang);
  const filterCategories = useMapStore((state) => state.filterCategories);
  const map = useMapStore((state) => state.map);
  const routeFinish = useMapStore((state) => state.routeFinish);
  const routeStart = useMapStore((state) => state.routeStart);
  const activeFilter = useMapStore((state) => state.activeFilter);
  const activeKiosk = useMapStore((state) => state.activeKiosk);
  const accessibleRoute = useMapStore((state) => state.accessibleRoute);
  const forceAccessibleRoute = useMapStore(
    (state) => state.forceAccessibleRoute,
  );
  const appSession = useMapStore((state) => state.appSession);
  const mobileMode = useMapStore((state) => state.mobileMode);
  const startFeatureStore = useMapStore((state) => state.startFeature);
  const features = useFeaturesStore((state) => state.features);
  const amenities = useMapStore((state) => state.amenities);
  const navMode = useMapStore((state) => state.navMode);
  const cityStart = useMapStore((state) => state.cityStart);
  const cityFinish = useMapStore((state) => state.cityFinish);
  const mapLoaded = useMapStore((state) => state.mapLoaded);
  const showCustomRoutePicker = useMapStore(
    (state) => state.showCustomRoutePicker,
  );
  const stops = useMapStore((state) => state.stops);
  const cameraRecognitionFeature = useMapStore(
    (state) => state.cameraRecognitionFeature,
  );
  const haveRouteDetails = useMapStore((state) => state.haveRouteDetails);
  const hideControls = useMapStore((state) => state.hideControls);
  const verticalKioskMode = useMapStore((state) => state.verticalKioskMode);
  const resetView = useMapStore((state) => state.resetView);

  // store actions
  const setMap = useMapStore(useShallow((state) => state.setMap));
  const setMapLoaded = useMapStore(useShallow((state) => state.setMapLoaded));
  const setPlaces = useMapStore((state) => state.setPlaces);
  const setCurrentPlace = useMapStore((state) => state.setCurrentPlace);
  const setFloors = useMapStore((state) => state.setFloors);
  const setKiosks = useMapStore((state) => state.setKiosks);
  const setCurrentFloor = useMapStore((state) => state.setCurrentFloor);
  const setFeatures = useFeaturesStore((state) => state.setFeatures);
  const setAmenities = useMapStore((state) => state.setAmenities);
  const setRouteStart = useMapStore((state) => state.setRouteStart);
  const setRouteFinish = useMapStore((state) => state.setRouteFinish);
  const setHaveRouteDetails = useMapStore((state) => state.setHaveRouteDetails);
  const setHaveRoutePreview = useMapStore((state) => state.setHaveRoutePreview);
  const setRouteDetails = useMapStore((state) => state.setRouteDetails);
  const setCurrentStep = useMapStore((state) => state.setCurrentStep);
  const setActiveFilter = useMapStore((state) => state.setActiveFilter);
  const setActiveKiosk = useMapStore((state) => state.setActiveKiosk);
  const setAccessibleRoute = useMapStore((state) => state.setAccessibleRoute);
  const setShowCustomRoutePicker = useMapStore(
    (state) => state.setShowCustomRoutePicker,
  );
  const setIsLoading = useMapStore((state) => state.setIsLoading);
  const locateMe = useMapStore((state) => state.locateMe);
  const setKioskMode = useMapStore((state) => state.setKioskMode);
  const setStepAnimated = useMapStore((state) => state.setStepAnimated);
  const centerToFinish = useMapStore((state) => state.centerToFinish);
  const setNavMode = useMapStore((state) => state.setNavMode);
  const setCityStart = useMapStore((state) => state.setCityStart);
  const setCityFinish = useMapStore((state) => state.setCityFinish);
  const setAllFloors = useMapStore((state) => state.setAllFloors);
  const setDataFetched = useMapStore((state) => state.setDataFetched);
  const setAuthSuccess = useMapStore((state) => state.setAuthSuccess);
  const setActiveStop = useMapStore((state) => state.setActiveStop);
  const setStartFeature = useMapStore((state) => state.setStartFeature);
  const setAppIsIdle = useMapStore((state) => state.setAppIsIdle);
  const setHaveCustomPosition = useMapStore(
    (state) => state.setHaveCustomPosition,
  );
  const setReachedDestination = useMapStore(
    (state) => state.setReachedDestination,
  );
  const setDBAds = useMapStore((state) => state.setDBAds);
  const setMapPrepared = useMapStore((state) => state.setMapPrepared);
  const setGpsMode = useMapStore((state) => state.setGpsMode);
  const setActiveMenuItem = useMapStore((state) => state.setActiveMenuItem);

  const prevRouteFinish = useRef<Feature>();
  const animationFrame = useRef<number>();
  const timeout = useRef<ReturnType<typeof setTimeout>>();
  const [alreadyRouted, setAlreadyRouted] = useState<boolean>(false);
  const [status, setStatus] = useState("");
  const poiMapClick = useRef(false);
  const { fetchActiveAds } = useFetchActiveAds();
  const [deviceBearing, setDeviceBearing] = useState<number | undefined>(
    undefined,
  );
  const [throwDebug] = useDebugger();

  const mapPadding = {
    top: mobileMode ? 50 : 130,
    bottom: mobileMode ? 200 : 130,
    left: mobileMode ? 50 : currentLang === "ar" ? 250 : 650,
    right: mobileMode ? 50 : currentLang === "ar" ? 650 : 250,
  };

  const zoom = mobileMode
    ? 17
    : verticalKioskMode &&
        import.meta.env.VITE_WAYFINDING_KIOSK_SCALING === "false"
      ? import.meta.env.VITE_WAYFINDING_DEFAULT_ZOOM + 1
      : import.meta.env.VITE_WAYFINDING_DEFAULT_ZOOM;
  const pitch = mobileMode ? 26 : import.meta.env.VITE_WAYFINDING_DEFAULT_PITCH;
  const bearing = mobileMode
    ? -87
    : import.meta.env.VITE_WAYFINDING_DEFAULT_BEARING;
  const defaultLocation = {
    coordinates: mobileMode
      ? [35.52963318079892, 33.86218846575949]
      : [
          +import.meta.env.VITE_WAYFINDING_DEFAULT_LOCATION_LONGITUDE,
          +import.meta.env.VITE_WAYFINDING_DEFAULT_LOCATION_LATITUDE,
        ],
    level: +import.meta.env.VITE_WAYFINDING_DEFAULT_LOCATION_LEVEL,
  };
  const entryLongitude = import.meta.env.VITE_WAYFINDING_MALL_ENTRY_LONGITUDE;
  const entryLatitude = import.meta.env.VITE_WAYFINDING_MALL_ENTRY_LATITUDE;

  const idleTime = routeFinish?.id ? 60e3 : 40e3;
  const appIsIdle = useIdle(idleTime); // 60/40 seconds to idle time

  const proximiLogoSVG = `
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 46.94 57.51" width="16" height="16" style="display:inline; margin-top: -2px;">
      <path fill="#1e1e1e" d="M40.3,6.54C36.01,2.32,30.03,0,23.47,0S10.93,2.32,6.64,6.54C2.36,10.75,0,16.61,0,23.02c0,5.22,2.23,10.65,6.83,16.6,3.3,4.28,13.46,16.13,13.89,16.64.7.82,1.71,1.26,2.74,1.26.42,0,.84-.07,1.24-.22,1.42-.52,2.36-1.87,2.37-3.38l.04-8.12c3.47-.47,6.72-1.63,9.53-3.41,1.07-.68,2.09-1.45,3.02-2.3,4.69-4.26,7.27-10.32,7.27-17.07s-2.36-12.27-6.64-16.48ZM29.04,30.73c-.69.51-1.45.94-2.28,1.25-.73.27-1.5.45-2.3.54-.31.03-.63.05-.94.05h-.01c-4.68,0-8.69-3.54-9.17-8.29-.33-3.19,1.02-6.17,3.32-8.07,1.47-1.36,3.46-2.14,5.81-2.14,1.81,0,3.41.47,4.72,1.31.01,0,.02.01.03.02,2.43,1.44,4.18,3.98,4.49,7.01.34,3.35-1.15,6.46-3.66,8.33Z"/>
    </svg>
  `;

  // finish select handler function
  const finishSelectHandler = useCallback(() => {
    console.log("routeFinish", routeFinish);
    // center the map, set the floor level to poi
    map.setFloorByLevel(routeFinish.properties.level);
    map.enablePolygonPreventedIcons();
    if (
      (!getHiddenItems().includes(routeFinish.properties.amenity) &&
        routeFinish.properties.hideIcon !== "hide") ||
      routeFinish.properties.metadata?.bays ||
      routeFinish.properties?.metadata?.prevent_polygon === true ||
      routeFinish.properties?.metadata?.prevent_polygon === "true"
    ) {
      map.setFeatureFilter(routeFinish.id);
      map.setFeaturesHighlight(
        [routeFinish.id],
        "#000000",
        58,
        0.5,
        true,
        0.5,
        [0, 2],
      );
      map.disablePolygonPreventedIcons();
      map.disablePolygons();
    } else {
      map.setFeaturesHighlight([]);
      setActiveFilter({} as FilterItemModel);
    }

    // setShowCustomRoutePicker(false);
    if (mobileMode && !poiMapClick.current) {
      if (routeFinish.properties?._dynamic?.polygon_id) {
        map.getMapboxInstance().jumpTo({
          center: getPolygonCentroid({
            polygonId: routeFinish.properties._dynamic.polygon_id as string,
            features: map.getMapState().allFeatures.features,
          }) as LngLatLike,
          zoom: 16.5,
          padding: {
            top: 0,
            right: 0,
            left: 0,
            bottom: 250,
          },
        });
      } else {
        map.getMapboxInstance().jumpTo({
          center: routeFinish.geometry.coordinates as LngLatLike,
          zoom: 16.5,
          padding: {
            top: 0,
            right: 0,
            left: 0,
            bottom: 250,
          },
        });
      }
    }

    /*if (kioskMode) {
      centerToFinish();
    }*/
    setTimeout(() => {
      setIsLoading(false);
      poiMapClick.current = false;
    }, 1000);
  }, [
    map,
    routeFinish,
    setActiveFilter,
    // setShowCustomRoutePicker,
    setIsLoading,
    mobileMode,
  ]);

  const routeCancelHandler = useCallback(() => {
    console.log("routeFinish cancelled");
    if (Object.keys(map).length > 0) {
      // cancel route if it's rendered
      map.cancelRoute();
      // remove polygon selection after route finish is cancelled
      map.handlePolygonSelection();
      map.setFeaturesHighlight([]);
      map.enablePolygonPreventedIcons();
      if (!forceAccessibleRoute) {
        setAccessibleRoute({ accessibleRoute: false });
      }
      const params = new URLSearchParams(location.search);
      if (alreadyRouted && kioskMode && mobileMode && params.get("kiosk")) {
        map.stopKiosk();
        setKioskMode(false);
        params.delete("kiosk");
        params.delete("qrCode");
        history.replaceState(null, "", "?" + params + location.hash);

        if (import.meta.env.VITE_WAYFINDING_USE_GPS_LOCATION === "true") {
          setGpsMode(true);
          map.initGpsMode();
        }
      }
    }
  }, [
    map,
    kioskMode,
    mobileMode,
    forceAccessibleRoute,
    alreadyRouted,
    setAccessibleRoute,
    setKioskMode,
    setGpsMode,
  ]);

  const polygonPrevented = useCallback((feature: Feature) => {
    return (
      feature.properties?.metadata?.prevent_polygon === true ||
      feature.properties?.metadata?.prevent_polygon === "true"
    );
  }, []);

  // This effect hook handles route finish state changes
  useEffect(() => {
    if (!mapLoaded) {
      return;
    }
    if (routeFinish?.id) {
      if (prevRouteFinish?.current?.id !== routeFinish?.id) {
        setActiveMenuItem("map");
        // set timeout to postpone some stuff and show poi info first
        setTimeout(() => {
          finishSelectHandler();
        }, 500);
      }
    } else {
      if (prevRouteFinish?.current?.id) {
        // set timeout to postpone some stuff and show poi info first
        setTimeout(() => {
          routeCancelHandler();
        }, 500);
      }
    }
  }, [
    routeFinish,
    map,
    finishSelectHandler,
    routeCancelHandler,
    currentLang,
    mobileMode,
    mapLoaded,
    setActiveMenuItem,
  ]);

  // This effect hook handles route start state changes
  useEffect(() => {
    if (Object.keys(map).length === 0) {
      return;
    }
    const urlParams = new URLSearchParams(window.location.search);
    console.log("start efffect", routeStart, startFeatureStore);
    if (
      ((kioskMode && activeKiosk) || cameraRecognitionFeature?.id) &&
      routeFinish?.id
    ) {
      findRoute({
        finish: routeFinish.id,
        preview: true,
      });
    }
    if (routeStart === "kiosk" || routeStart?.id) {
      console.log("routeStart", routeStart);
      // if we also have route finish generate route and reset the current step
      if (routeFinish?.id) {
        findRoute({
          finish: routeFinish.id,
          start: routeStart === "kiosk" ? undefined : routeStart.id,
          stops,
        });
        setCurrentStep(0);
        setActiveStop(0);
        setTimeout(() => {
          setStepAnimated(0);
        }, 2000);
        if (urlParams.get("followRoute") === "true" || mobileMode) {
          if (
            activeKiosk?.latitude ||
            (typeof routeStart === "object" &&
              routeStart?.geometry?.coordinates)
          ) {
            // handles zoom level for mobile mode
            map.getMapboxInstance().jumpTo({
              center:
                routeStart === "kiosk"
                  ? ([
                      activeKiosk?.longitude,
                      activeKiosk?.latitude,
                    ] as LngLatLike)
                  : (routeStart.geometry.coordinates as LngLatLike),
              zoom: 17.5,
            });
          }
        }
        map.handlePolygonSelection(
          routeStart !== "kiosk" && routeStart.properties?._dynamic?.polygon_id
            ? [
                polygonPrevented(routeFinish) ? undefined : routeFinish,
                polygonPrevented(routeStart) ? undefined : routeStart,
                ...stops.map((stop) => stop.feature),
              ]
            : polygonPrevented(routeFinish)
              ? [
                  routeStart as Feature,
                  undefined,
                  ...stops.map((stop) => stop.feature),
                ]
              : [
                  routeStart as Feature,
                  routeFinish,
                  ...stops.map((stop) => stop.feature),
                ],
        );
        setAlreadyRouted(true);
        return;
      }
    }
    if (startFeatureStore.id) {
      findRoute({
        finish: routeFinish.id,
        start: startFeatureStore.id,
        stops,
        preview: true,
      });
      if (
        polygonPrevented(routeFinish) &&
        !startFeatureStore.properties?._dynamic?.polygon_id
      ) {
        return;
      }
      map.handlePolygonSelection(
        routeStart !== "kiosk" && routeStart.properties?._dynamic?.polygon_id
          ? [
              polygonPrevented(routeFinish) ? undefined : routeFinish,
              polygonPrevented(routeStart) ? undefined : routeStart,
              ...stops.map((stop) => stop.feature),
            ]
          : polygonPrevented(routeFinish)
            ? undefined
            : [routeFinish, ...stops.map((stop) => stop.feature)],
      );
      return;
    }
  }, [
    routeStart,
    routeFinish,
    stops,
    map,
    activeKiosk,
    kioskMode,
    findRoute,
    setCurrentStep,
    setActiveStop,
    setStepAnimated,
    startFeatureStore,
    mobileMode,
    polygonPrevented,
    cameraRecognitionFeature,
  ]);

  // This effect hook handles active filter state changes
  useEffect(() => {
    console.log("active filter effect");
    setIsLoading(true);

    // set timeout to postpone some stuff and show poi info first
    setTimeout(() => {
      if (Object.keys(map).length > 0) {
        if (activeFilter?.id) {
          const foundCategory = filterCategories.find((category) =>
            category.items.find((items) => items.id === activeFilter.id),
          );
          console.log("active filter", activeFilter);
          // map.resetAmenityFilters();
          // workaround for keeping hidden all pois within the category hidden
          const hiddenCategory = filterCategories.find(
            (i) => i.id === "hidden",
          )!;
          map.setAmenityFilter(
            compareValues(hiddenCategory.items[0]!.id!, activeFilter!.id!)
              ? activeFilter!.id!
              : "nonexisting",
            "hidden",
          );

          map.setAmenityFilter(activeFilter!.id!, foundCategory!.title);
          // workaround to hide all poi icons by setting feature filter to polygon feature
          if (foundCategory?.id !== "facilities") {
            // find polygon prevented features under active amenity filter
            const polygonPrevented = features
              .filter((f) => {
                const activeFilterId = Array.isArray(activeFilter.id)
                  ? activeFilter.id
                  : [activeFilter.id];
                return (
                  (f.properties?.metadata?.prevent_polygon === true ||
                    f.properties?.metadata?.prevent_polygon === "true") &&
                  activeFilterId.includes(f.properties.amenity)
                );
              })
              .map((f) => f.id);
            if (polygonPrevented.length > 0) {
              for (const id of polygonPrevented) {
                // set them as active feature filter to keep their icons visible
                map.setFeatureFilter(id);
              }
            } else {
              // if there are no polygon prevented features set the active filter to polygon feature to hide all icons
              map.setFeatureFilter(
                "3c1f630a-bec8-4a29-b790-16c60d4afd79:12c92660-3bb8-4552-8b2b-4921a41cf549",
              );
            }
          }
          setIsLoading(false);
        } else {
          console.log("active filter cancelled", activeFilter, routeFinish);
          if (Object.keys(map).length > 0) {
            map.resetAmenityFilters();
            map.resetFeatureFilters();

            // workaround for hiding all pois within the category hidden
            map.setAmenityFilter("nonexisting", "hidden");

            // handle polygon selection, only required when polygons are enabled
            if (routeFinish?.id) {
              setTimeout(() => {
                if (
                  routeFinish.properties?.metadata?.prevent_polygon === true ||
                  routeFinish.properties?.metadata?.prevent_polygon === "true"
                ) {
                  map.setFeatureFilter(routeFinish.id);
                } else {
                  map.handlePolygonSelection(
                    routeStart !== "kiosk" &&
                      routeStart.properties?._dynamic?.polygon_id
                      ? [
                          polygonPrevented(routeFinish)
                            ? undefined
                            : routeFinish,
                          polygonPrevented(routeStart) ? undefined : routeStart,
                          ...stops.map((stop) => stop.feature),
                        ]
                      : polygonPrevented(routeFinish)
                        ? [
                            routeStart as Feature,
                            undefined,
                            ...stops.map((stop) => stop.feature),
                          ]
                        : [
                            routeStart as Feature,
                            routeFinish,
                            ...stops.map((stop) => stop.feature),
                          ],
                  );
                  map.setFeatureFilter(routeFinish.id);
                }
              });
            } else {
              map.enablePolygonPreventedIcons();
            }
            setTimeout(() => {
              setIsLoading(false);
            }, 1000);
          }
        }
      }
    }, 500);
  }, [
    map,
    activeFilter,
    filterCategories,
    routeFinish,
    setIsLoading,
    polygonPrevented,
    features,
    stops,
  ]);

  // This effect hook language change
  useEffect(() => {
    console.log("change language", currentLang);
    i18n.changeLanguage(currentLang);
    if (Object.keys(map).length > 0) {
      map.setLanguage(currentLang);
      map.setBoundsPadding({
        top: mobileMode ? 50 : 130,
        bottom: mobileMode ? 200 : 130,
        left: mobileMode ? 50 : currentLang === "ar" ? 250 : 650,
        right: mobileMode ? 50 : currentLang === "ar" ? 650 : 250,
      });
    }
    const rootHtml = document.getElementById("root-html");
    if (rootHtml) {
      rootHtml.setAttribute("dir", currentLang === "ar" ? "rtl" : "ltr");
      rootHtml.setAttribute("data-dir", currentLang === "ar" ? "rtl" : "ltr");
    }
  }, [currentLang, map, mobileMode]);

  useEffect(() => {
    if (!mapLoaded) {
      return;
    }
    prevRouteFinish.current = routeFinish;
  }, [routeFinish, mapLoaded]);

  const rotateCamera = useCallback(
    (timestamp: number) => {
      // clamp the rotation between 0 -360 degrees
      // Divide timestamp by 100 to slow rotation to ~10 degrees / sec
      if (Object.keys(map).length > 0) {
        map
          .getMapboxInstance()
          .rotateTo((timestamp / 300) % 360, { duration: 0 });
      }
      // Request the next frame of the animation.
      animationFrame.current = requestAnimationFrame(rotateCamera);
    },
    [map],
  );

  // This effect hook set reset app session on idle time
  useEffect(() => {
    if (!mobileMode && kioskMode) {
      if (appIsIdle) {
        if (Object.keys(map).length > 0) {
          map.getMapboxInstance().flyTo({
            center: defaultLocation.coordinates as [number, number],
            pitch: 40,
            zoom: 17,
            duration: 2000,
          });
          map.getMapboxInstance().touchZoomRotate.disable();
          map.getMapboxInstance().touchPitch.disable();
          map.getMapboxInstance().dragPan.disable();
          map.getMapboxInstance().dragRotate.disable();
          timeout.current = setTimeout(() => {
            rotateCamera(0);
          }, 2300);
          setAppIsIdle(true);
        }
      } else {
        clearTimeout(timeout.current);
        cancelAnimationFrame(animationFrame.current!);
        setAppIsIdle(false);
        if (Object.keys(map).length > 0) {
          setTimeout(() => {
            locateMe();
          }, 100);
          setTimeout(() => {
            map.getMapboxInstance().touchZoomRotate.enable();
            map.getMapboxInstance().touchPitch.enable();
            map.getMapboxInstance().dragPan.enable();
            map.getMapboxInstance().dragRotate.enable();
          }, 1000);
        }
      }
    }
  }, [
    appIsIdle,
    bearing,
    map,
    mobileMode,
    rotateCamera,
    locateMe,
    setAppIsIdle,
  ]);

  useEffect(() => {
    // Function to set the height of body and #proximiioMap
    const setHeight = () => {
      document.body.style.height = `${window.innerHeight}px`;
      const proximiioMap = document.getElementById("proximiioMap");
      if (proximiioMap) {
        proximiioMap.style.height = `${window.innerHeight}px`;
      }
    };

    // Set the height on initial load
    setHeight();

    // Add event listener for window resize to update heights
    window.addEventListener("resize", setHeight);

    // Cleanup event listener on component unmount
    return () => {
      window.removeEventListener("resize", setHeight);
    };
  }, []);

  useEffect(() => {
    // let lastHeadingUpdate = 0;
    let lastLocationUpdate = 0;
    const handleMessage = (
      event: MessageEvent<
        | {
            type: string;
            id?: string;
            latitude?: number;
            longitude?: number;
            level?: number;
            heading?: number;
          }
        | string
      >,
    ) => {
      // Check the message's origin for security purposes
      // if (event.origin !== "https://trusted-origin.com") return;

      if (Object.keys(map).length > 0) {
        const { type, id, latitude, longitude, level, heading } =
          typeof event.data === "string" ? JSON.parse(event.data) : event.data;

        const eventFeature = id
          ? features.find(
              (f) =>
                f.id?.toLowerCase() === id?.toLowerCase() ||
                f.properties.id?.toLowerCase() === id?.toLowerCase() ||
                f.properties.title?.toLowerCase() === id?.toLowerCase() ||
                f.properties.remote_id?.toString().toLowerCase() ===
                  id?.toLowerCase(),
            )
          : undefined;

        switch (type) {
          case "SET_DESTINATION": {
            console.log("set destination", id, eventFeature);
            if (eventFeature) {
              setRouteFinish(eventFeature);
              centerToFinish();
            }
            break;
          }
          case "SET_START": {
            console.log("set start", id, eventFeature);
            if (eventFeature) {
              setRouteStart(eventFeature);
            }
            break;
          }
          case "SET_LOCATION": {
            if (routeFinish?.id && !haveRouteDetails) {
              return;
            }
            const now = Date.now();
            if (now - lastLocationUpdate < 3000) {
              console.log("throttled location update");
              return; // throttle: skip if less than 2 seconds
            }
            if (level !== undefined && latitude && longitude) {
              console.log("set position", deviceBearing);
              map.setCustomPosition({
                coordinates: [longitude, latitude],
                level,
                recenter: haveRouteDetails,
                iconSize: 1.3,
                directionIconSize: 0.8,
              });
              setHaveCustomPosition(true);
              lastLocationUpdate = now;
            }
            break;
          }
          case "SET_HEADING": {
            /*const now = Date.now();
            if (now - lastHeadingUpdate < 100) {
              console.log("throttled heading update");
              return; // throttle: skip if less than 100ms
            }*/
            if (heading) {
              console.log("set heading", heading);
              map.setCustomPositionHeading({
                heading,
                followBearing: false,
              });
              // lastHeadingUpdate = now;
            }
            break;
          }
          default: {
            console.warn("Unknown message type:", type);
          }
        }
      }
    };

    // Add the event listener to handle messages
    window.addEventListener("message", handleMessage);

    // Clean up the event listener when the component unmounts
    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, [
    features,
    map,
    haveRouteDetails,
    setRouteFinish,
    setRouteStart,
    centerToFinish,
    setHaveCustomPosition,
    routeFinish,
    deviceBearing,
  ]); // Empty dependency array ensures this effect runs once on mount

  useEffect(() => {
    if (Object.keys(map).length > 0) {
      buildAdditionalFilters({
        features,
        amenities,
        language: currentLang,
      });

      // set amenity category group from store/data.ts
      for (const category of filterCategories) {
        map.setAmenitiesCategory(
          category.title,
          category.items.map((i) => (i.id ? i.id : "undefined")).flat(2),
        );
      }

      map.setHiddenAmenities(getHiddenItems() as string[]);
    }
  }, [amenities, features, currentLang, map, filterCategories]);

  useEffect(() => {
    if (Object.keys(map).length > 0) {
      if (
        cityStart?.coordinates?.length > 0 &&
        cityFinish?.coordinates?.length > 0
      ) {
        const combinedRoute = cityStart.poiId || cityFinish.poiId;
        const startAtMall = cityStart.poiId;

        if (combinedRoute) {
          map.findCombinedRoute({
            start: startAtMall
              ? cityStart.poiId!
              : {
                  lng: cityStart.coordinates[0],
                  lat: cityStart.coordinates[1],
                },
            connectingPoint: {
              lng: entryLongitude,
              lat: entryLatitude,
            },
            destination: !startAtMall
              ? cityFinish.poiId!
              : {
                  lng: cityFinish.coordinates[0],
                  lat: cityFinish.coordinates[1],
                },
            autoStart: haveRouteDetails,
            accessibleRoute: accessibleRoute,
            wayfindingConfig: getWayfindingConfig(accessibleRoute),
          });
        } else {
          map.hideIcons();
          map.findCityRoute({
            start: {
              lng: cityStart.coordinates[0],
              lat: cityStart.coordinates[1],
            },
            destination: {
              lng: cityFinish.coordinates[0],
              lat: cityFinish.coordinates[1],
            },
            autoStart: false,
          });
        }

        setCurrentStep(0);
        setTimeout(() => {
          setStepAnimated(0);
        }, 2000);
      } else {
        map.cancelRoute();
        map.showIcons();
        if (kioskMode) locateMe();
      }
    }
  }, [cityStart, cityFinish]);

  useEffect(() => {
    if (
      kioskMode &&
      activeKiosk?.longitude &&
      activeKiosk?.latitude &&
      typeof activeKiosk?.level !== "undefined" &&
      Object.keys(map).length > 0
    ) {
      map.setKiosk(
        activeKiosk?.latitude,
        activeKiosk?.longitude,
        activeKiosk?.level,
      );
      if (routeFinish?.id) {
        findRoute({
          finish: routeFinish.id,
          preview: true,
        });
      }
      if (autoRouting) {
        setTimeout(() => {
          setRouteStart("kiosk");
        }, 1000);
      }
    }
  }, [
    activeKiosk,
    autoRouting,
    findRoute,
    map,
    routeFinish,
    setRouteStart,
    kioskMode,
  ]);

  useEffect(() => {
    const handleOrientation = (event: DeviceOrientationEvent) => {
      // @ts-expect-error ios
      if (event.webkitCompassHeading) {
        // some devices don't understand "alpha" (especially IOS devices)
        // @ts-expect-error ios
        setDeviceBearing(event.webkitCompassHeading);
      } else if (
        event.alpha !== null &&
        event.beta !== null &&
        event.gamma !== null
      ) {
        const heading = compassHeading(event.alpha, event.beta, event.gamma);
        setDeviceBearing(heading);
      }
    };

    window.addEventListener("deviceorientation", handleOrientation, false);

    return () => {
      window.removeEventListener("deviceorientation", handleOrientation, false);
    };
  }, [setDeviceBearing]);

  useEffect(() => {
    // Initialize map only once
    if (mapInitiated.current) return;
    mapInitiated.current = true;

    let routeLoading = false;
    let kiosk = {} as KioskModel;
    const urlParams = new URLSearchParams(window.location.search);

    const initMap = () => {
      setStatus("");
      throwDebug({ message: "Initializing map started" });
      // Authenticate Proximiio.js library and create map instance with provided constructor options
      Proximiio.Auth.setToken(import.meta.env.VITE_PROXIMIIO_TOKEN)
        .then(() => {
          throwDebug({ message: "Authentication successfull", success: true });
          setAuthSuccess(true);
          setStatus("INIT_SUCCESS");
          // when authenticated create a map instance with provided constuctor options, check out library readme https://github.com/proximiio/proximiio-js-library#map-component
          const map = new Proximiio.Map({
            bundleUrl:
              import.meta.env.MODE !== "development"
                ? import.meta.env.VITE_PROXIMIIO_BUNDLE_URL
                : undefined,
            // you can define any of the mapbox options in there, we are retrieving those from state service
            mapboxOptions: {
              center: defaultLocation.coordinates as [number, number],
              zoom: zoom,
              pitch:
                kioskMode && activeKiosk?.pitch ? activeKiosk.pitch : pitch,
              bearing:
                kioskMode && activeKiosk?.bearing
                  ? activeKiosk.bearing
                  : bearing,
            },
            defaultPlaceId: defaultPlaceId, // if you have more than 1 place in your account, it's a good idea to define defaultPlaceId for the map, otherwise the first one will be picked up
            isKiosk: kioskMode, // if enabled starting point for routing will be based on values defined in kioskSettings, if disabled findRoute methods will expect start point to be send.
            kioskSettings: {
              coordinates:
                kioskMode && activeKiosk?.longitude && activeKiosk?.latitude
                  ? [activeKiosk.longitude, activeKiosk.latitude]
                  : (defaultLocation.coordinates as [number, number]),
              level:
                kioskMode && activeKiosk?.level
                  ? activeKiosk.level
                  : defaultLocation.level,
              showPoint: true,
              showLabel: true,
              pointColor: "255,92,57",
              labelFont: ["Quicksand", "sans-serif"],
            },
            fitBoundsPadding: mapPadding, // setting the padding option to use for zooming into the bounds when route is drawn,
            minFitBoundsDistance: 5,
            handleUrlParams: true, // enable handling url params, this way you can load map with predefined route generated
            urlParams: {
              destinationFeature: "remote_id",
              autoRouting,
            },
            language: currentLang, // init with predefined language setting
            useGpsLocation: gpsMode, // if enabled your location will be detected with geolocation API and used as a starting point for routing
            geolocationControlOptions: {
              autoTrigger: true, // if enabled map will automatically enable geolocation
              autoLocate: false, // if enabled map will automatically focus on user location
              position: "top-right", //  position on the map to which the control will be added.
            },
            showLevelDirectionIcon: true, // if enabled arrow icon will be shown at the levelchanger indicating direction of level change along the found route
            levelDirectionOutlineColor: "#FF5C39",
            levelDirectionPopupImage:
              "data:image/png;base64,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",
            initPolygons: true,
            polygonsOptions: {
              defaultPolygonColor: "#00C1D5", // optional, default: '#dbd7e8', default color of the polygons
              hoverPolygonColor: "#00C1D5", // optional, default: '#a58dfa', hover color of the polygons
              selectedPolygonColor: "#00C1D5", // optional, default: '#6945ed', selected color of the polygons
              defaultLabelColor: "#000000", // optional, default: '#6945ed', default color of the polygon labels
              hoverLabelColor: "#000000",
              selectedLabelColor: "#000000",
              defaultPolygonHeight: 5, // optional, default: 3, default polygon height in meters
              hoverPolygonHeight: 6, // optional, default: 3, hover polygon height in meters
              selectedPolygonHeight: 6, // optional, default: 3, selected polygon height in meters
              removeOriginalPolygonsLayer: true,
              minZoom: 15,
              labelMinZoom: 18,
              iconMinZoom: 16,
              disabledPolygonColor: "#999",
              disabledLabelColor: "rgba(0,0,0, 1)",
              disabledPolygonHeight: 4,
              adaptiveLabelOpacity: true,
              adaptiveMaxPitch: 26,
              drawRouteUnderPolygons: true,
              textFont: ["Quicksand Regular", "Noto Sans Arabic Regular"],
              labelFontSize: [
                "interpolate",
                ["exponential", 1.75],
                ["zoom"],
                18, // Zoom level 18
                [
                  "interpolate",
                  ["linear"],
                  [
                    "/",
                    ["get", "length", ["get", "_dynamic"]],
                    ["/", ["length", ["get", "title"]], 1.5],
                  ],
                  1,
                  13, // Map smallest font size
                  10,
                  25, // Map medium font size
                  15,
                  35, // Map largest font size
                ],
                22, // Zoom level 22
                [
                  "interpolate",
                  ["linear"],
                  [
                    "/",
                    ["get", "length", ["get", "_dynamic"]],
                    ["/", ["length", ["get", "title"]], 1.5],
                  ],
                  1,
                  23, // Map smallest font size at higher zoom
                  10,
                  35, // Map medium font size at higher zoom
                  15,
                  45, // Map largest font size at higher zoom
                ],
              ],
              bufferDistance: +import.meta.env
                .VITE_WAYFINDING_POLYGON_BUFFER_DISTANCE,
              typesToScale: JSON.parse(
                import.meta.env.VITE_WAYFINDING_POLYGONS_TO_SCALE,
              ),
            },
            polygonLayers: [
              {
                featureType: "shop",
              },
              {
                featureType: "shop-booth",
                defaultPolygonHeight: 2,
              },
              {
                featureType: "anchor-shop",
                iconImage: "{id}",
              },
              {
                featureType: "dining",
                defaultPolygonColor: "#FF5C39",
                hoverPolygonColor: "#FF5C39",
                selectedPolygonColor: "#FF5C39",
              },
              {
                featureType: "dining-booth",
                defaultPolygonHeight: 2,
                defaultPolygonColor: "#FF5C39",
                hoverPolygonColor: "#FF5C39",
                selectedPolygonColor: "#FF5C39",
              },
              {
                featureType: "entertainment",
                defaultPolygonColor: "#9021B9",
                hoverPolygonColor: "#9021B9",
                selectedPolygonColor: "#9021B9",
              },
              {
                featureType: "anchor-entertainment",
                iconImage: "{id}",
                defaultPolygonColor: "#9021B9",
                hoverPolygonColor: "#9021B9",
                selectedPolygonColor: "#9021B9",
              },
              {
                featureType: "stay",
                defaultPolygonColor: "#005CAA",
                hoverPolygonColor: "#005CAA",
                selectedPolygonColor: "#005CAA",
              },
              {
                featureType: "facilities",
                defaultPolygonColor: "#FEBF3B",
                hoverPolygonColor: "#FEBF3B",
                selectedPolygonColor: "#FEBF3B",
              },
              {
                featureType: "parking_bays0",
                defaultPolygonColor: "#383838",
                hoverPolygonColor: "#383838",
                selectedPolygonColor: "#383838",
                disabledPolygonColor: "#383838",
                defaultPolygonHeight: 0.5,
                hoverPolygonHeight: 1,
                selectedPolygonHeight: 1,
                disabledPolygonHeight: 0.5,
                defaultLabelColor: "rgba(0,0,0,0)",
                selectedLabelColor: "rgba(0,0,0,0)",
                hoverLabelColor: "rgba(0,0,0,0)",
                disabledLabelColor: "rgba(0,0,0,0)",
                opacity: 0.4,
                layerId: "parking_bays",
              },
            ],
            routeAnimation: {
              enabled: true,
              type: "point",
              followRoute: true,
              followRouteAngle: false,
              looping: false,
              pointIconUrl:
                "https://pub-dc411ce328ca438c8229e569659d6ebb.r2.dev/walkingmanorange.png",
              pointIconAsMarker: true,
              pointIconMarkerSize: mobileMode ? 300 * 0.175 : 300 * 0.25,
              durationMultiplier: mobileMode ? 0.8 : 1.5,
              lineColor: "#FF5C39",
              lineWidth: 10,
              minzoom: 10,
              cityPointIconUrl:
                "https://proximi.ams3.digitaloceanspaces.com/car-circle.png",
              cityRouteSpeedMultiplier: 20,
              cityRouteMaxDuration: 7,
              autoStart: false,
              autoRestart: false,
              autoContinue: !landmarkTBT || !mobileMode,
              cityRouteZoom: 15,
              mallRouteZoom: 18,
              mallEntryLevel: +import.meta.env.VITE_WAYFINDING_MALL_ENTRY_LEVEL,
              stepChangeThreshold: 10,
              showCompassDirection: true,
            },
            routeColor: "#FFB2A2",
            blockFeatureClickWhileRouting: true,
            defaultFloorLevel: +import.meta.env
              .VITE_WAYFINDING_DEFAULT_LOCATION_LEVEL,
            zoomIntoPlace: false,
            hiddenAmenities: getHiddenItems() as string[],
            useRasterTiles: true,
            rasterTilesOptions: {
              useProxy: import.meta.env.MODE === "development",
              minZoom: 12,
              maxZoom: 20,
              bounds: [
                35.528050144235976, 33.86025091879253, 35.53104656360574,
                33.86382090427716,
              ],
            },
            /*featuresMaxBounds: [
              [55.437980892653286, 25.17300768646389],
              [55.32613455606534, 25.30698731225813],
            ],*/
            autoLevelChange: true,
            poiIconSize: [
              "interpolate",
              ["exponential", 0.5],
              ["zoom"],
              17,
              0.25,
              22,
              0.7,
            ],
            pmTilesUrl:
              import.meta.env.MODE !== "development"
                ? import.meta.env.VITE_PROXIMIIO_PMTILES_URL
                : undefined,
            landmarkTBTNavigation: landmarkTBT,
            apiPaginate: true,
            bundlePaginate: true,
            /*localSources: {
						features:
							urlParams.get('useLocalSources') === 'true'
								? new FeatureCollection(featuresJSON)
								: new FeatureCollection([]),
						amenities:
							urlParams.get('useLocalSources') === 'true'
								? amenitiesJSON as AmenityModel[]
								: [] as AmenityModel[],
					},*/
          });

          map.getMapLoadListener().subscribe(() => {
            console.log("map prepared");
            throwDebug({ message: "Map prepared", success: true });
            setMapPrepared(true);
          });

          map.getMapFailedListener().subscribe((error) => {
            throwDebug({
              message: `Map failed: ${error.message}`,
              error: true,
            });
          });

          map.getMapReadyListener().subscribe((ready) => {
            console.log("map ready", ready);
            throwDebug({ message: "Map ready", success: true });

            // setting mapbox navigationControl buttons
            map.getMapboxInstance().addControl(
              new maplibregl.NavigationControl({
                showZoom: false,
              }),
            );

            /*map.getMapboxInstance().addControl(
              new maplibregl.AttributionControl({
                customAttribution: `
                  <a href="https://proximi.io/" target="_blank">${proximiLogoSVG} Proximi.io</a> |
                  <a href="https://maplibre.org/" target="_blank">MapLibre</a>
                `,
                compact: true,
              }),
              "bottom-right",
            );*/

            map.getMapboxInstance().setMaxZoom(20);
            map.getMapboxInstance().on("touchstart", (e) => {
              if (e.originalEvent.touches.length > 2) {
                // e.preventDefault(); // Prevent default touch behavior
              }
            });

            setMap(map);

            // start route animation and cancel loading state when route is fully loaded
            map.getMapboxInstance().on("sourcedata", (ev) => {
              if (
                ev.sourceId === "route" &&
                ev.isSourceLoaded &&
                (ev.source as { data: FeatureCollection }).data.features
                  .length > 0
              ) {
                if (routeLoading) {
                  setIsLoading(false);
                  routeLoading = false;
                  map.restartRouteAnimation({ delay: 500, recenter: false });
                }
              }
            });
          });

          map.getDataFetchedListener().subscribe(() => {
            console.log("data fetched listener");
            throwDebug({ message: "Map data fetched", success: true });
            const mapState: State = map.getMapState();

            const places = mapState.places;
            setPlaces(places);
            setCurrentPlace(mapState.place);
            setFloors(
              mapState.floors.filter((f) => f.placeId === mapState.place.id),
            );
            setAllFloors(mapState.allFloors);
            setCurrentFloor(mapState.floor);
            const features = mapState.allFeatures.features.filter(
              (feature) =>
                (feature.properties.usecase === "poi" ||
                  feature.properties.type === "poi") &&
                feature.geometry.type === "Point" &&
                feature.properties.type !== "escalator" &&
                feature.properties.type !== "elevator" &&
                feature.properties.type !== "staircase" &&
                feature.properties.place_id === defaultPlaceId &&
                feature.properties.title?.trim() !== "" &&
                feature.properties.available !== false,
            );
            setFeatures(features);
            setAmenities(mapState.amenities);
            const kiosks = [
              ...predefinedKiosks,
              ...mapState.kiosks.map((kiosk) => {
                return {
                  ...kiosk,
                  latitude: kiosk.coordinates.lat,
                  longitude: kiosk.coordinates.lng,
                };
              }),
            ];
            setKiosks(kiosks);
            const ads = mapState.ads;
            setDBAds(ads);

            // This function handles URL query parameters related to start, destination and place
            setTimeout(() => {
              const startParam = urlParams.get("startFeature"); // in case you change url param name in urlParams option of map constuctor, change that too
              const destinationParam = urlParams.get("remote_id"); // in case you change url param name in urlParams option of map constuctor, change that too
              const placeParam = urlParams.get("defaultPlace"); // in case you change url param name in urlParams option of map constuctor, change that too

              const navModeParam = urlParams.get("navMode");
              const cityStartParam = urlParams.get("cityStart");
              const cityFinishParam = urlParams.get("cityFinish");

              const startFeature = startParam
                ? features.find(
                    (f) =>
                      f.id?.toLowerCase() === startParam?.toLowerCase() ||
                      f.properties.id?.toLowerCase() ===
                        startParam?.toLowerCase() ||
                      f.properties.title?.toLowerCase() ===
                        startParam?.toLowerCase() ||
                      f.properties.remote_id?.toString().toLowerCase() ===
                        startParam?.toLowerCase(),
                  )
                : undefined;
              if (startFeature) {
                if (!navModeParam || navModeParam === "mall") {
                  setStartFeature(startFeature);
                } else {
                  setCityStart({
                    title: startFeature.properties?.title
                      ? startFeature.properties?.title
                      : "",
                    coordinates: startFeature.geometry.coordinates,
                    poiId: startFeature.id,
                  });
                  if (cityFinishParam) {
                    setRouteStart(startFeature);
                  }
                }
              }

              const destinationFeature = destinationParam
                ? features.find(
                    (f) =>
                      f.id?.toLowerCase() === destinationParam?.toLowerCase() ||
                      f.properties.id?.toLowerCase() ===
                        destinationParam?.toLowerCase() ||
                      f.properties.title?.toLowerCase() ===
                        destinationParam?.toLowerCase() ||
                      f.properties.remote_id?.toString().toLowerCase() ===
                        destinationParam?.toLowerCase(),
                  )
                : undefined;
              if (destinationFeature) {
                if (!navModeParam || navModeParam === "mall") {
                  setRouteFinish(destinationFeature);
                } else {
                  setCityFinish({
                    title: destinationFeature.properties?.title
                      ? destinationFeature.properties?.title
                      : "",
                    coordinates: destinationFeature.geometry.coordinates,
                    poiId: destinationFeature.id,
                  });
                  if (cityStartParam) {
                    setRouteFinish(destinationFeature);
                  }
                }
              }

              const defaultPlace = placeParam
                ? places.find(
                    (p) => p.id === placeParam || p.name === placeParam,
                  )
                : undefined;
              if (defaultPlace) {
                map.setPlace(defaultPlace.id);
              }

              if (
                navModeParam &&
                (navModeParam === "mall" ||
                  navModeParam === "city-to" ||
                  navModeParam === "city-from")
              ) {
                setNavMode(navModeParam);
                if (!cityStartParam && !cityFinishParam) {
                  setShowCustomRoutePicker(true);
                }
              }

              if (cityStartParam) {
                const cityStartParamParsed = JSON.parse(cityStartParam) as {
                  title: string;
                  coordinates: number[];
                };
                if (
                  cityStartParamParsed?.title &&
                  cityStartParamParsed?.coordinates
                ) {
                  setCityStart({
                    title: cityStartParamParsed.title,
                    coordinates: cityStartParamParsed.coordinates,
                  });
                }
              }

              if (cityFinishParam) {
                const cityFinishParamParsed = JSON.parse(cityFinishParam) as {
                  title: string;
                  coordinates: number[];
                };
                if (
                  cityFinishParamParsed?.title &&
                  cityFinishParamParsed?.coordinates
                ) {
                  setCityFinish({
                    title: cityFinishParamParsed.title,
                    coordinates: cityFinishParamParsed.coordinates,
                  });
                }
              }

              if (kiosks.length > 0) {
                // handle kiosk mode intitialization
                if (urlParams.get("kiosk")) {
                  kiosk = kiosks.find(
                    (kiosk) =>
                      kiosk.name?.toLowerCase() ===
                        urlParams.get("kiosk")?.toLowerCase() ||
                      kiosk.id?.toLowerCase() ===
                        urlParams.get("kiosk")?.toLowerCase(),
                  ) as KioskModel;

                  const kioskPoi = features.find((f) => f.id === kiosk?.poiId);

                  if (
                    kiosk?.bounds &&
                    kiosk.bounds.length === 2 &&
                    kiosk.bounds[0].length === 2 &&
                    kiosk.bounds[1].length === 2
                  ) {
                    map.getMapboxInstance().setMaxBounds(kiosk.bounds);
                  }
                  if (kiosk?.zoom) {
                    map
                      .getMapboxInstance()
                      .setZoom(
                        verticalKioskMode &&
                          import.meta.env.VITE_WAYFINDING_KIOSK_SCALING ===
                            "false"
                          ? kiosk.zoom + 1
                          : kiosk.zoom,
                      );
                  }
                  if (kiosk?.bearing) {
                    map.getMapboxInstance().setBearing(kiosk.bearing);
                  }
                  if (kiosk?.pitch) {
                    map.getMapboxInstance().setPitch(kiosk.pitch);
                  }

                  if (kiosk) {
                    if (kioskPoi) {
                      kiosk.longitude = kioskPoi.geometry.coordinates[0];
                      kiosk.latitude = kioskPoi.geometry.coordinates[1];
                      kiosk.level = kioskPoi.properties.level;
                    }
                  }

                  setActiveKiosk(kiosk);
                }
              }

              // save feature select log for qr code scans
              const qrParam = urlParams.get("qrCode");
              if (qrParam) {
                let featureAmenityCategory = "";
                const featureSection = destinationFeature?.properties?.amenity
                  ? filterCategories.find((category) =>
                      category.items.find((item) => {
                        if (
                          (Array.isArray(item.id) &&
                            item.id.includes(
                              destinationFeature.properties.amenity,
                            )) ||
                          item.id === destinationFeature.properties.amenity
                        ) {
                          featureAmenityCategory = item.title;
                          return true;
                        }
                      }),
                    )
                  : undefined;

                console.log("LOG: save qr scan log");
                const userData = {
                  osName,
                  browserName,
                  mobileModel,
                  mobileVendor,
                  deviceType:
                    kioskMode && !mobileMode && kiosk?.name
                      ? kiosk?.name
                      : deviceType,
                };
                new Proximiio.InteractionLogger({
                  interactionType: "qr",
                  targetElementType: destinationFeature?.id
                    ? "feature"
                    : undefined,
                  targetElementTitle: destinationFeature?.properties?.title
                    ? destinationFeature.properties.title
                    : undefined,
                  targetElementId: destinationFeature?.id
                    ? destinationFeature.id
                    : undefined,
                  targetElementAmenity: featureAmenityCategory
                    ? t(featureAmenityCategory, { lng: "en" })
                    : undefined,
                  targetElementAmenityCategory: featureSection
                    ? t(featureSection.title, { lng: "en" })
                    : undefined,
                  kioskId: kiosk?.id ? kiosk.id : undefined,
                  kioskTitle: kiosk?.name ? kiosk.name : undefined,
                  metadata: userData,
                  language: currentLang,
                  session: appSession,
                });
              }
            });

            setDataFetched(true);
            throwDebug({ message: "Data set to the core" });
          });

          map.getMainSourceLoadedListener().subscribe(() => {
            console.log("map fully loaded");
            throwDebug({ message: "Map fully loaded", success: true });
            setMapLoaded(true);

            const attribContainer = document.querySelector(
              ".maplibregl-ctrl-attrib-inner",
            );
            if (attribContainer) {
              attribContainer.innerHTML = `
                <a href="https://proximi.io/" target="_blank">${proximiLogoSVG} Proximi.io</a> |
                <a href="https://maplibre.org/" target="_blank">MapLibre</a> |
                <a href="http://www.openmaptiles.org/" target="_blank">© OpenMapTiles</a> <a href="http://www.openstreetmap.org/about/" target="_blank">© OpenStreetMap contributors</a>
              `;
            }
          });

          // set data refetching interval if enabled in .env file
          if (refetchData) {
            setInterval(
              () => {
                map.refetch();
                fetchActiveAds();
              },
              +import.meta.env.VITE_WAYFINDING_REFETCH_INTERVAL,
            );
          }

          // set destination point for routing based on click event and cancel previous route if generated
          map.getPolygonClickListener().subscribe((feature) => {
            if (prevRouteFinish?.current?.id !== feature?.id) {
              setIsLoading(true);
              setRouteFinish(feature);
              poiMapClick.current = true;
            }

            //save feature click log
            console.log("LOG: save feature click log");
            let featureAmenityCategory = "";
            const featureSection = feature?.properties?.amenity
              ? filterCategories.find((category) =>
                  category.items.find((item) => {
                    if (
                      (Array.isArray(item.id) &&
                        item.id.includes(feature.properties.amenity)) ||
                      item.id === feature.properties.amenity
                    ) {
                      featureAmenityCategory = item.title;
                      return true;
                    }
                  }),
                )
              : undefined;

            const userData = {
              osName,
              browserName,
              mobileModel,
              mobileVendor,
              deviceType:
                kioskMode && !mobileMode && kiosk?.name
                  ? kiosk?.name
                  : deviceType,
            };

            new Proximiio.InteractionLogger({
              interactionType: "mapclick",
              targetElementType: "feature",
              targetElementId: feature ? feature.id : undefined,
              targetElementTitle: feature
                ? feature.properties.title
                : undefined,
              targetElementAmenity: t(featureAmenityCategory, { lng: "en" }),
              targetElementAmenityCategory: featureSection
                ? t(featureSection.title, { lng: "en" })
                : "",
              kioskId: kiosk?.id ? kiosk?.id : undefined,
              kioskTitle: kiosk?.name ? kiosk?.name : undefined,
              metadata: userData,
              language: currentLang,
              session: appSession,
            });
          });

          // subscribe to map place selection listener, this always run once at map initiation and upon map.setPlace method call
          map.getPlaceSelectListener().subscribe((place) => {
            setCurrentPlace(place);
          });

          // subscribe to map floor selection listener, this always run once at map initiation and upon map.setFloor method call
          map.getFloorSelectListener().subscribe((floor) => {
            setCurrentFloor(floor);
          });

          // subscribe to route found listener
          map.getRouteFoundListener().subscribe((res) => {
            console.log("route found", res);
            // map.stopRouteAnimation();
            if (res.details && res.TBTNav) {
              if (res.preview) {
                setHaveRoutePreview(true);
                setHaveRouteDetails(false);
              } else {
                // stop route animation and set loading state, until route is fully loaded, will be handled in sourcedata listener
                setIsLoading(true);
                routeLoading = true;
                setHaveRoutePreview(false);
                setHaveRouteDetails(true);
              }
              setRouteDetails(res);
              setAlreadyRouted(true);
            }
          });

          // subscribe to route failed listener
          map.getRouteFailedListener().subscribe((res) => {
            console.log("route failed", res);
            // if route found failed, try again with non/accessible route
            setAccessibleRoute({ accessibleRoute: !accessibleRoute });
          });

          // subscribe to nav step listener and set current step from that
          map.getNavStepSetListener().subscribe((step) => {
            setCurrentStep(step);
            /*setTimeout(() => {
              setStepAnimated(step);
            }, 2000);*/
          });

          // subscribe to nav stop listener and set current stop from that
          map.getStopSetListener().subscribe((stop) => {
            setActiveStop(stop);
          });

          map.getArrivalListener().subscribe(() => {
            console.log("ARRIVAL");
            setReachedDestination(true);
            setTimeout(() => {
              resetView();
            }, 10000);
          });
        })
        .catch((err) => {
          console.error("LOGIN FAILED", err);
          throwDebug({ message: "Authentification failed", error: true });
          setStatus("INIT_FAILED");
          setTimeout(initMap, 120e3);
        });
    };

    initMap();
  });

  return (
    <>
      {status === "INIT_FAILED" ? (
        <div className="z-[200] mx-auto flex items-center justify-center">
          <MapFailedAlert />
        </div>
      ) : (
        <>
          <div
            id="proximiioMap"
            style={{ font: "inherit" }}
            className={cn(
              "absolute h-screen w-full overflow-hidden group-data-[dir=ltr]:ml-0 group-data-[dir=rtl]:mr-0",
              showCustomRoutePicker &&
                navMode !== "mall" &&
                (!cityStart?.title || !cityFinish?.title) &&
                "pointer-events-none",
              hideControls && "hide-controls",
            )}
          ></div>
        </>
      )}
    </>
  );
}
export default MapView;
